import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import CopyButton from '../../commonUI/CopyButton';

type InfoItem = {
  label: string;
  value: string;
  showCopyButton?: boolean;
  icon?: React.ReactNode;
};

type Props = {
  title: string;
  items: InfoItem[];
  maxWidth?: number;
};

/**
 * A generic tooltip component for displaying information with copy functionality
 *
 * @param title - The title displayed at the top of the tooltip
 * @param items - Array of information items to display, each with:
 *   - label: The descriptive text shown above the value
 *   - value: The actual data to display and potentially copy
 *   - showCopyButton: (Optional) Whether to show copy button (defaults to true)
 *   - icon: (Optional) React node to display as an icon before the label
 * @param maxWidth - (Optional) Maximum width of the tooltip in pixels (defaults to 500)
 * @returns A tooltip component displaying the provided information
 *
 * @example
 * <TooltipGenericInfo
 *   title="Customer Info"
 *   items={[
 *     { label: "ID", value: "12345", icon: <IdIcon /> },
 *     { label: "Email", value: "user@example.com" },
 *     { label: "Status", value: "Active", showCopyButton: false }
 *   ]}
 * />
 */
export const TooltipGenericInfo = ({ title, items, maxWidth = 500 }: Props) => {
  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 2, overflow: 'hidden', maxWidth, p: 1 }}
    >
      <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
          sx={{ mb: 0.75 }}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={0.75}>
          {items.map((item, index) => (
            <InfoField
              key={`${item.label}-${index}`}
              label={item.label}
              value={item.value}
              showCopyButton={item.showCopyButton ?? true}
              icon={item.icon}
            />
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};

type InfoFieldProps = {
  label: string;
  value: string;
  showCopyButton?: boolean;
  icon?: React.ReactNode;
};

/**
 * An individual information field with label, value, and optional copy button
 */
const InfoField = ({
  icon,
  label,
  value,
  showCopyButton = true,
}: InfoFieldProps) => {
  return (
    <Box
      sx={{
        py: 0.75,
        px: 0.75,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction='row' spacing={1.5} alignItems='flex-start'>
        {icon && <Box sx={{ color: 'primary.main', opacity: 0.9 }}>{icon}</Box>}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ lineHeight: 1.3 }}
          >
            {label}
          </Typography>

          <Stack
            direction='row'
            alignItems='center'
            spacing={0.5}
            sx={{ mt: 0.25 }}
          >
            <Typography
              variant='body2'
              fontWeight={500}
              sx={{ lineHeight: 1.3, wordBreak: 'break-all' }}
            >
              {value}
            </Typography>

            {showCopyButton && value && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                <CopyButton textToCopy={value} />
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
